import React, { createContext, useContext, useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { checkGeoBlocking } from '../utils/geoBlocking';
import EUWaitlistPage from '../pages/EUWaitlistPage';

const GeoBlockingContext = createContext();

export const useGeoBlocking = () => {
  const context = useContext(GeoBlockingContext);
  if (!context) {
    throw new Error('useGeoBlocking must be used within GeoBlockingProvider');
  }
  return context;
};

export const GeoBlockingProvider = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkUserLocation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if user is from blocked region
        const blocked = await checkGeoBlocking();
        setIsBlocked(blocked);

        // Log the check for analytics (non-PII)
        console.log(`Geo-blocking check completed: ${blocked ? 'blocked' : 'allowed'}`);
        
      } catch (err) {
        console.error('Geo-blocking check failed:', err);
        setError(err.message);
        // Fail open - allow access if check fails
        setIsBlocked(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserLocation();
  }, []);

  // Show loading spinner while checking
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Checking availability in your region...
        </Typography>
      </Box>
    );
  }

  // Show error message if geo-blocking check failed
  if (error) {
    console.warn('Geo-blocking failed, allowing access:', error);
    // Fail open - show the app if geo-blocking fails
  }

  // Show EU waitlist page if user is blocked
  if (isBlocked) {
    return <EUWaitlistPage />;
  }

  // Provide context value for non-blocked users
  const value = {
    isBlocked: false,
    isLoading: false,
    error
  };

  return (
    <GeoBlockingContext.Provider value={value}>
      {children}
    </GeoBlockingContext.Provider>
  );
};